import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  ServiceTypeEnum,
  adminPutLocationReqDtoSchema,
  useAdminLocationControllerDelete,
  useAdminLocationControllerGetOne,
  useAdminLocationControllerUpdate,
} from "service";
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ControlledSelect, makeSxStyles } from "ui";
import {
  AppAccessEnum,
  appAccessEnumSelectOptions,
} from "service/types/enum/app-access-enum";
import { useFormik } from "formik";
import { useDialogStore } from "../../../state/dialog-store";
import { toast } from "react-toastify";
import { AdminPutLocationReqDto } from "service/api/estabrookSchemas";
import { Delete, ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import { toFormikValidationSchema } from "zod-formik-adapter";
import useEditStore from "../../../state/edit-store";
import { z } from "zod";
import CrmDetailsFields, {
  CrmFieldValues,
} from "@/shared/components/crm/CrmDetailsFields";
import CustomerFinancesSection from "@/shared/components/crm/CustomerFinancesSection";
import { fieldLabelSx } from "@/shared/components/crm/form-typography";
import { crmFieldValidations } from "@/shared/utils/crm-validation";
import {
  numericFieldSx,
  numericInputProps,
} from "@/shared/utils/numeric-input";
import SkippedOrdersPerSeason from "@/features/customers/components/SkippedOrdersPerSeason";
import PlowReportDialog from "../customer-management-combo/plow-report-dialog";
// import { isNull, omitBy } from "lodash";
// import useRegions from "../../../hooks/use-regions";

// Extend the generated schema to enforce minimum serviceTime of 1
const customPutLocationSchema = adminPutLocationReqDtoSchema.extend({
  serviceTime: z
    .number()
    .min(1, "Service duration must be at least 1 minute")
    .optional(),
  // Validate CRM fields (held flat in the form) when a value is present.
  ...crmFieldValidations,
});

// The generated locationDetails response types CRM fields as Record<string,
// any>, but the API returns primitives — coerce when seeding the form.
const asString = (value: unknown): string | undefined =>
  value == null ? undefined : String(value);
const asNumber = (value: unknown): number | undefined => {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

// Location fields (flat) + CRM fields (flat in the form, nested on submit).
type AddressFormValues = AdminPutLocationReqDto & CrmFieldValues;

const AddressDetailComponent = forwardRef<
  { updateAddres: () => void },
  { addressId: string; setTab?: () => void; customerServiceAccess?: boolean }
>(({ addressId, setTab, customerServiceAccess }, ref) => {
  const { isLoading, data } = useAdminLocationControllerGetOne(
    {
      pathParams: { locationId: addressId || "" },
    },
    {
      enabled: !!addressId,
    },
  );
  const { setDialog } = useDialogStore();
  // CRM sections collapse behind Show More (default collapsed, per Figma).
  const [showMore, setShowMore] = useState(false);

  const { setIsDirtyAddress, setIsValidAddress } = useEditStore();

  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    updateAddres: () => {
      submitForm();
    },
  }));

  const { mutate } = useAdminLocationControllerUpdate({
    onSuccess: () => {
      toast.success("Address Updated Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/locations/{locationId}",
          operationId: "adminLocationControllerGetOne",
          variables: {
            pathParams: { locationId: addressId || "" },
          },
        }),
      );

      // Invalidate customers-locations query so Customer Management page reflects changes
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/customers-locations",
          operationId: "adminCustomerLocationControllerGetAll",
          variables: {},
        }),
      );
    },
  });

  const { mutate: removeAddress } = useAdminLocationControllerDelete({
    onSuccess: () => {
      toast.success("Address Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/customers/{customerId}/locations",
          operationId: "adminLocationCustomerControllerGetAll",
          variables: {
            pathParams: { customerId: data?.data?.customerId || "" },
          },
        }),
      );
      setTab && setTab();
    },
  });

  const onUpdateAddress = () => {
    mutate({
      pathParams: { locationId: addressId || "" },
      body: {
        type: values.type,
        reportsEnabled: values.reportsEnabled,
        serviceTime: values.serviceTime,
        notes: values.notes,
        regionId: values.regionId,
        serviceAccess: values.serviceAccess,
        // CRM fields persist nested under locationDetails (not flat).
        locationDetails: {
          onsiteContactName: values.onsiteContactName,
          onsiteContactEmail: values.onsiteContactEmail,
          contractPrice: values.contractPrice,
          billingAddress: values.billingAddress,
          financialNotes: values.financialNotes,
          squareFootage: values.squareFootage,
          slope: values.slope,
          condition: values.condition,
          difficulty: values.difficulty,
        },
      },
    });
  };

  const onDeleteAddress = () => {
    setDialog({
      title: "Delete Address",
      description: `Are you sure you want to delete the address ${data?.data?.address}? This action cannot be undone.`,
      withCancelAction: true,
      cancelText: "Cancel",
      confirmText: "Delete",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        removeAddress({ pathParams: { locationId: addressId || "" } });
        onClose();
      },
    });
  };

  const {
    values,
    handleBlur,
    handleChange,
    dirty,
    isValid,
    resetForm,
    setFieldValue,
    setFieldTouched,
    submitForm,
    touched,
    errors,
  } = useFormik<AddressFormValues>({
    initialValues: {
      type: ServiceTypeEnum.onDemand,
      reportsEnabled: false,
      billingInformation: "",
      serviceTime: 0,
      notes: "",
      regionId: "",
      serviceAccess: true,
      // CRM fields (flat in the form; nested under locationDetails on submit)
      onsiteContactName: "",
      onsiteContactEmail: "",
      contractPrice: undefined,
      billingAddress: "",
      financialNotes: "",
      squareFootage: undefined,
      slope: undefined,
      condition: undefined,
      difficulty: undefined,
    },
    validationSchema: toFormikValidationSchema(customPutLocationSchema),
    onSubmit: () => onUpdateAddress(),
  });

  useEffect(() => {
    setIsDirtyAddress(dirty);
  }, [dirty]);

  useEffect(() => {
    setIsValidAddress(isValid);
  }, [isValid]);

  // A CRM value seeded from the API can fail the field rules (a price carrying
  // more than 2 decimals, a difficulty stored before it was constrained to a
  // whole 1–5). Those inputs sit behind "Show More", so a collapsed section
  // would gate Save (via isValidAddress) with nothing on screen explaining why.
  // Depending on the flag rather than `errors` means this fires on the
  // transition only, leaving the section collapsible again afterwards.
  const hasCrmNumericError = !!(
    errors.contractPrice ||
    errors.squareFootage ||
    errors.difficulty
  );

  useEffect(() => {
    if (hasCrmNumericError) setShowMore(true);
  }, [hasCrmNumericError]);

  // Populate form with API data when it loads
  useEffect(() => {
    if (!isLoading && data?.data) {
      const locationData = data.data;
      const details = locationData.locationDetails;
      resetForm({
        values: {
          type: locationData.type || ServiceTypeEnum.onDemand,
          reportsEnabled: locationData.reportsEnabled || false,
          billingInformation: locationData.billingInformation || "",
          serviceTime: locationData.serviceTime || 0,
          notes: locationData.notes || "",
          regionId: locationData.region?.id || "",
          serviceAccess: locationData.serviceAccess ?? true,
          onsiteContactName: asString(details?.onsiteContactName) ?? "",
          onsiteContactEmail: asString(details?.onsiteContactEmail) ?? "",
          contractPrice: asNumber(details?.contractPrice),
          billingAddress: asString(details?.billingAddress) ?? "",
          financialNotes: asString(details?.financialNotes) ?? "",
          squareFootage: asNumber(details?.squareFootage),
          slope: details?.slope || undefined,
          condition: details?.condition || undefined,
          difficulty: asNumber(details?.difficulty),
        },
      });
    }
  }, [data, isLoading, resetForm]);

  const styles = useStyles();

  const plowSkipReport = data?.data?.plowSkipReport;

  const handleDownloadReport = () => {
    setReportDialogOpen(true);
  };

  return (
    <Box sx={styles.customerDetailContainer}>
      <Typography sx={styles.sectionTitle}>Address Info</Typography>
      <Typography sx={fieldLabelSx}>Address *</Typography>
      <TextField
        id="street"
        type="text"
        margin="dense"
        variant="outlined"
        value={data?.data?.address || ""}
        fullWidth
        disabled
      />
      <Typography sx={fieldLabelSx}>Region</Typography>
      <TextField
        id="regionId"
        type="text"
        margin="dense"
        variant="standard"
        InputProps={{
          disableUnderline: true,
        }}
        sx={{
          minHeight: "53px",
          justifyContent: "center",
          margin: "0",
        }}
        value={data?.data?.region?.name || "Unassigned"}
        disabled
        fullWidth
      />
      <Typography sx={fieldLabelSx}>Service Duration *</Typography>
      <TextField
        id="serviceTime"
        type="number"
        margin="dense"
        variant="outlined"
        value={values.serviceTime}
        onBlur={handleBlur}
        onChange={handleChange}
        error={touched.serviceTime && !!errors.serviceTime}
        helperText={touched.serviceTime && errors.serviceTime}
        // Input guards only (no typed "-"/"e"/letters, no stepping). The value
        // still goes through Formik's handleChange, which stores "" for a
        // cleared field — this schema's serviceTime rule is `.optional()`, so
        // switching to the undefined the hook writes would let an empty
        // required field pass validation.
        inputProps={numericInputProps({ allowDecimal: true })}
        fullWidth
        InputProps={{
          endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
        }}
        sx={numericFieldSx}
      />
      <Typography sx={fieldLabelSx}>Snow Reporter</Typography>
      <Checkbox
        id="reportsEnabled"
        checked={values.reportsEnabled}
        sx={{ justifySelf: "flex-start" }}
        onChange={handleChange}
      />
      <Typography sx={fieldLabelSx}>Service Access</Typography>
      <ControlledSelect
        placeholder="Service Access"
        options={appAccessEnumSelectOptions}
        value={
          values.serviceAccess ? AppAccessEnum.active : AppAccessEnum.disabled
        }
        disabled={customerServiceAccess === false}
        onChange={(value) => {
          const isActive = value === AppAccessEnum.active;
          setFieldValue("serviceAccess", isActive);
          setFieldTouched("serviceAccess", true);
        }}
      />
      <SkippedOrdersPerSeason
        report={plowSkipReport}
        isLoading={isLoading}
        onDownloadReport={handleDownloadReport}
      />
      <PlowReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        locationIds={[addressId || ""]}
        subtitle={data?.data?.address}
        defaultToSeasonToDate
      />
      <Typography sx={fieldLabelSx} alignSelf="start">
        Delete Address
      </Typography>
      <Box display="grid" gap="20px" alignItems="flex-start">
        <Typography variant="caption">
          Before deleting this address, please note that doing so will result in
          the permanent loss of all related information.
        </Typography>
        <Button
          sx={styles.deleteButton}
          color="error"
          variant="outlined"
          onClick={onDeleteAddress}
          startIcon={<Delete />}
          data-testid="delete-address"
        >
          Delete Address
        </Button>
      </Box>
      {showMore && (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <CustomerFinancesSection
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
          <CrmDetailsFields
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            sectionOrder={["onsiteContact", "drivewayInfo"]}
          />
        </Box>
      )}
      <Box></Box>
      <Box>
        <Button
          sx={{ justifySelf: "flex-start" }}
          variant="text"
          onClick={() => setShowMore(!showMore)}
          disabled={isLoading}
          endIcon={showMore ? <ArrowDropUp /> : <ArrowDropDown />}
          data-testid={showMore ? "show-less-button" : "show-more-button"}
        >
          {showMore ? "Show Less" : "Show More"}
        </Button>
      </Box>
    </Box>
  );
});

const useStyles = makeSxStyles(() => ({
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
  },
  customerDetailContainer: {
    my: "40px",
    display: "grid",
    // Match FieldRow (label 260 / gap 24 / input 360) so Address Info aligns
    // with the Finances / Onsite / Driveway sections below.
    gridTemplateColumns: "260px 360px",
    columnGap: "24px",
    rowGap: "16px",
    alignItems: "center",
  },
  deleteButton: {
    maxWidth: "165px",
  },
}));

export default AddressDetailComponent;
